import cx from 'classnames'
import NextImage from 'next/image'
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type SanityFrameBuilderTextureImage } from '../../data/sanity/queries/types/texture'
import { getBasicImageLoader, getSanityImageUrl } from '../../lib/image'

export interface ImageSize {
  width?: number
  height?: number
}

interface ImageSlideProps {
  textureImage: SanityFrameBuilderTextureImage
  index: number
  selectedIndex: number
  setCurrentImageSize: Dispatch<SetStateAction<ImageSize>>
  className?: string
}

const ImageSlide = ({
  textureImage,
  index,
  selectedIndex,
  setCurrentImageSize,
  className,
}: ImageSlideProps) => {
  const [imageSize, setImageSize] = useState<ImageSize>({})

  const isImageLoaded = useRef<boolean>(false)

  const imageUrl = useMemo(
    () =>
      getSanityImageUrl(textureImage.image, {
        width: 1200,
      }),
    [textureImage],
  )

  // Update image size
  useEffect(() => {
    if (isImageLoaded.current || !imageUrl) {
      return
    }

    const image = new Image()
    image.src = imageUrl
    image.onload = () => {
      if (image.width && image.height) {
        setImageSize({
          width: image.width,
          height: image.height,
        })
      }

      isImageLoaded.current = true
    }
  }, [imageUrl, isImageLoaded, setCurrentImageSize])

  // Update current image width size when selected index changes
  useEffect(() => {
    if (index === selectedIndex && imageSize?.width && imageSize?.height) {
      setCurrentImageSize({
        width: imageSize.width,
        height: imageSize.height,
      })
    }
  }, [imageSize, index, selectedIndex, setCurrentImageSize])

  if (!imageUrl) {
    return
  }

  const aspectRatio =
    textureImage.image.asset?.metadata?.dimensions?.aspectRatio ?? 1
  const imageWidth = Math.min(
    textureImage.image.asset?.metadata?.dimensions?.width ?? 1200,
    1200,
  )
  const imageHeight = imageWidth / aspectRatio

  return (
    <div className={cx('w-full h-full sm:px-[100px]', className)}>
      <div className="relative w-full h-full">
        <NextImage
          src={imageUrl}
          width={imageWidth}
          height={imageHeight}
          alt={textureImage.alternativeText}
          loader={getBasicImageLoader}
          className="absolute top-1/2 left-1/2 w-auto max-w-full max-h-[80%] -translate-x-1/2 -translate-y-1/2"
        />
      </div>
    </div>
  )
}

export default ImageSlide
